import type { FormEvent } from "react";

import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { SeriesGameFields } from "./ScheduleEditorFields";
import type {
  SeriesEditorValue,
  SeriesMetadataEditorValue,
} from "./scheduleEditor";
import type { ActiveSchedulePlayer } from "./scheduleTypes";

export function SeriesEditorCard({
  value,
  players,
  serverBusy,
  pending = serverBusy,
  unknown = false,
  onChange,
  onCancel,
  onSubmit,
}: {
  value: SeriesEditorValue;
  players: ActiveSchedulePlayer[];
  serverBusy: boolean;
  pending?: boolean;
  unknown?: boolean;
  onChange: (value: SeriesEditorValue) => void;
  onCancel: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const updateGame = (
    index: number,
    game: SeriesEditorValue["games"][number],
  ) => {
    const games = [...value.games];
    games[index] = game;
    onChange({ ...value, games });
  };
  return (
    <Card>
      <CardContent className="grid gap-4 pt-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="m-0 font-mono text-sm font-bold tracking-[0.1em] text-warning uppercase">
              ADD PLAYOFF SERIES
            </h2>
            <p className="mt-1 mb-0 text-xs text-foreground-muted">
              Best of 3 · all game slots are created together
            </p>
          </div>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            onClick={onCancel}
            disabled={pending}
          >
            CANCEL
          </Button>
        </div>
        <form
          className="grid gap-4"
          onSubmit={onSubmit}
          aria-busy={pending || undefined}
        >
          <fieldset disabled={pending} className="contents">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1 text-sm font-bold">
                SERIES NAME
                <Input
                  aria-label="Series name"
                  value={value.title}
                  onChange={(event) =>
                    onChange({ ...value, title: event.target.value })
                  }
                  required
                />
              </label>
              <label className="grid gap-1 text-sm font-bold">
                OPPONENT
                <Input
                  aria-label="Series opponent"
                  value={value.opponentName}
                  onChange={(event) =>
                    onChange({ ...value, opponentName: event.target.value })
                  }
                  placeholder="Opponent"
                  required
                />
              </label>
            </div>
            {value.games.map((game, index) => (
              <fieldset
                key={index}
                className="grid gap-3 rounded-[var(--radius-sm)] border border-border p-3"
              >
                <legend className="px-1 font-mono text-xs font-bold tracking-[0.08em] text-warning">
                  GAME {index + 1}
                  {index === 2 ? " · IF NECESSARY" : ""}
                </legend>
                <SeriesGameFields
                  gameNumber={index + 1}
                  value={game}
                  players={players}
                  onChange={(next) => updateGame(index, next)}
                />
              </fieldset>
            ))}
            <Button
              type="submit"
              size="scoring"
              pending={pending}
              pendingLabel={unknown ? "CHECKING…" : "CREATING…"}
            >
              CREATE BEST-OF-3 SERIES
            </Button>
          </fieldset>
        </form>
      </CardContent>
    </Card>
  );
}

export function SeriesMetadataEditorCard({
  value,
  serverBusy,
  pending = serverBusy,
  unknown = false,
  onChange,
  onCancel,
  onSubmit,
}: {
  value: SeriesMetadataEditorValue;
  serverBusy: boolean;
  pending?: boolean;
  unknown?: boolean;
  onChange: (value: SeriesMetadataEditorValue) => void;
  onCancel: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <Card>
      <CardContent className="grid gap-3 pt-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="m-0 font-mono text-sm font-bold tracking-[0.1em] text-warning uppercase">
            EDIT SERIES
          </h2>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            onClick={onCancel}
            disabled={pending}
          >
            CANCEL
          </Button>
        </div>
        <form
          className="grid gap-3"
          onSubmit={onSubmit}
          aria-busy={pending || undefined}
        >
          <fieldset disabled={pending} className="contents">
            <label className="grid gap-1 text-sm font-bold">
              SERIES NAME
              <Input
                aria-label="Series name"
                value={value.title}
                onChange={(event) =>
                  onChange({ ...value, title: event.target.value })
                }
                required
              />
            </label>
            <label className="grid gap-1 text-sm font-bold">
              OPPONENT
              <Input
                aria-label="Series opponent"
                value={value.opponentName}
                onChange={(event) =>
                  onChange({ ...value, opponentName: event.target.value })
                }
                required
              />
            </label>
            <Button
              type="submit"
              size="scoring"
              pending={pending}
              pendingLabel={unknown ? "CHECKING…" : "SAVING…"}
            >
              SAVE SERIES
            </Button>
          </fieldset>
        </form>
      </CardContent>
    </Card>
  );
}
